import { createApi } from '@reduxjs/toolkit/query/react';

import {
  IUserTrainingAccessGetResponse,
  IUserTrainingAccessPostBody,
} from '../../types/interfaces/user-training-access';
import { HTTP_METHOD, REDUX_CACHE, USERS } from '../constants';
import * as endpoints from '../endpoints';
import {
  TransformedErrorResponseWithNotification,
  getBaseQuery,
} from '../generic';

const defaultAccessResponse = (): IUserTrainingAccessGetResponse => ({
  createTraining: [],
  sharePrograms: [],
  shareTrainings: [],
});

const transformAccessResponse = (
  response: IUserTrainingAccessGetResponse,
): IUserTrainingAccessGetResponse => ({
  createTraining: response?.createTraining ?? [],
  sharePrograms: response?.sharePrograms ?? [],
  shareTrainings: response?.shareTrainings ?? [],
});

export const userTrainingAccessApi = createApi({
  reducerPath: 'userTrainingAccessApi',
  tagTypes: [USERS.USER_TRAINING_ACCESS, REDUX_CACHE.ABORT_INVALIDATION],
  baseQuery: getBaseQuery(),
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getUserTrainingAccessOutgoingApi: builder.query<
      IUserTrainingAccessGetResponse,
      void
    >({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.UserTrainingAccess.OUTGOING,
          method: HTTP_METHOD.GET,
        };
      },
      transformResponse: transformAccessResponse,
      providesTags: () => [
        { type: USERS.USER_TRAINING_ACCESS, id: 'OUTGOING' },
      ],
      ...TransformedErrorResponseWithNotification(),
    }),
    getUserTrainingAccessIncomingApi: builder.query<
      IUserTrainingAccessGetResponse,
      void
    >({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.UserTrainingAccess.INCOMING,
          method: HTTP_METHOD.GET,
        };
      },
      transformResponse: transformAccessResponse,
      providesTags: () => [
        { type: USERS.USER_TRAINING_ACCESS, id: 'INCOMING' },
      ],
      ...TransformedErrorResponseWithNotification(),
    }),
    postUserTrainingAccessOutgoingApi: builder.mutation<
      IUserTrainingAccessGetResponse,
      IUserTrainingAccessPostBody
    >({
      query(body) {
        return {
          url: endpoints.UserTrainingAccess.OUTGOING,
          method: HTTP_METHOD.POST,
          body,
        };
      },
      transformResponse: transformAccessResponse,
      invalidatesTags: () => [
        { type: USERS.USER_TRAINING_ACCESS, id: 'OUTGOING' },
      ],
      ...TransformedErrorResponseWithNotification(),
    }),
  }),
});

export const {
  useGetUserTrainingAccessOutgoingApiQuery: useGetUserTrainingAccessOutgoing,
  useGetUserTrainingAccessIncomingApiQuery: useGetUserTrainingAccessIncoming,
  usePostUserTrainingAccessOutgoingApiMutation:
    usePostUserTrainingAccessOutgoing,
} = userTrainingAccessApi;

export const resetUserTrainingAccessApiState = () =>
  userTrainingAccessApi.util.resetApiState();
